<script lang="ts" setup>
import RandomBanner from "~/features/banners/RandomBanner.vue";
import BrandInfo from "~/widgets/brands/brand/BrandInfo.vue";
import ProductCategories from "~/widgets/brands/brand/ProductCategories.vue";
import BrandCompilation from "~/widgets/brands/brand/BrandCompilation.vue";
import ProductsSection from "~/widgets/brands/brand/ProductsSection.vue";
import NewProducts from "~/widgets/brands/brand/NewProducts.vue";
import Breadcrumbs from "~/components/ui/breadcrumbs/Breadcrumbs.vue";
import BackLink from "~/components/ui/links/BackLink.vue";
import useMainStore from "~/shared/store";
import type {IBreadcrumbs} from "~/shared/interfaces/ui/breadcrumbs";
import type {IBrand, IBrandCompilationItem, IBrandInfo} from "~/shared/interfaces/brands";
import type {ICatalogProduct} from "~/shared/interfaces/catalog";

definePageMeta({
  layout: 'main-page'
})

const route = useRoute()
const mainStore = useMainStore()
const productCategories = ref<IBrand[] | null>(null)
const newProducts = ref<ICatalogProduct[] | null>(null)
const popularProducts = ref<ICatalogProduct[] | null>(null)
const recommendedProducts = ref<ICatalogProduct[] | null>(null)
const lastSeenProducts = ref<ICatalogProduct[] | null>(null)
const currentBrand = ref<IBrandInfo | null>(null)
const brandCompilation = ref<IBrandCompilationItem[] | null>(null)
const breadcrumbs = ref<IBreadcrumbs[]>([
  {name: 'Бренды', slug: 'brands'},
  {name: route.params.id as string}
])

let query: {brandId: number}

watch(currentBrand, () => {
  if (currentBrand.value) {
    query = {
      brandId: currentBrand.value.id
    }
  }
})

async function getBrandInfo() {
  const {data} = await useFetch<IBrandInfo>(`/api/brands/${route.params.id}`)
  if (data.value) {
    if (data.value?.id) {
      query = {brandId: data.value?.id}
    }

    currentBrand.value = data.value as IBrandInfo
  } else {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
  }
}

async function getCategories() {
  const {data} = await useFetch('/api/brands/getProductCategories', {
    query
  })

  productCategories.value = data.value
}


async function getBrandCompilation() {
  const {data} = await useFetch('/api/brands/getBrandCompilation', {
    query
  })

  brandCompilation.value = data.value
}
async function getProductsSection(path: string, reference: Ref) {
  const {data} = await useFetch(path)
  reference.value = data.value
}

mainStore.setContentLoadingStatus(false)
await getBrandInfo()
await getCategories()
await getBrandCompilation()
await getProductsSection('/api/brands/getNewProducts', newProducts)
await getProductsSection('/api/brands/getPopularProducts', popularProducts)
await getProductsSection('/api/brands/getRecommendationProducts', recommendedProducts)
await getProductsSection('/api/brands/getLastSeenProducts', lastSeenProducts)
mainStore.setContentLoadingStatus(true)
</script>

<template>
  <RandomBanner api-endpoint="search"/>
  <Breadcrumbs :breadcrumbs="breadcrumbs"/>
  <BackLink/>
  <BrandInfo
    v-if="currentBrand"
    :brand-info="currentBrand"
  />
  <ProductCategories
    v-if="productCategories?.length"
    :cards="productCategories"
  />
  <NewProducts
    v-if="newProducts?.length"
    :products="newProducts"
  />
  <div class="brand-tag container">
    <BrandCompilation
      v-if="brandCompilation?.length"
      :items="brandCompilation"
    />
  </div>
  <ProductsSection
    v-if="popularProducts?.length"
    section-title="Популярные товары"
    :products="popularProducts"
  />
  <ProductsSection
    v-if="recommendedProducts?.length"
    section-title="Рекомендуем"
    :products="recommendedProducts"
  />
  <ProductsSection
    v-if="lastSeenProducts?.length"
    section-title="Вы смотрели"
    :products="lastSeenProducts"
    :is-card-without-button="false"
  />
</template>

<style lang="scss" scoped>
.back-link {
  display: none;
  @include sm-breakpoint {
    display: block;
  }
}

.breadcrumbs {
  padding-left: 28px;
  @include sm-breakpoint {
    padding-left: 20px;
    display: none;
  }

  :deep(.breadcrumbs__list-item) {
    &:last-child {
      text-transform: uppercase;
    }
  }
}

.brand-tag.container {
  @include md-breakpoint {
    padding: 0;
  }
}

:deep(h2) {
  @include font(28px, 32px, 600);
  margin-bottom: 28px;
  @include md-breakpoint {
    margin-bottom: 24px;
    padding: 0 20px;
    @include font(20px, 24px, 600);
  }
}

section {
  margin-top: 60px;
  @include sm-breakpoint {
    margin-top: 40px;
  }
}
</style>
